import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useResolvedSellerId } from "@/hooks/useResolvedSellerId";

const PAGE_SIZE = 20;

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url?: string;
  created_at: string;
}

interface Seller {
  full_name: string;
  business_name?: string;
  email?: string;
  phone_number?: string;
  profile_image_url?: string;
}

export const usePublicSellerListings = (
  sellerIdOrSlug: string,
  statusFilter: 'active' | 'pending' = 'active'
) => {
  const { resolvedId: sellerId, isResolving, error: resolveError } = useResolvedSellerId(sellerIdOrSlug);
  const [properties, setProperties] = useState<Property[]>([]);
  const [seller, setSeller] = useState<Seller | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const fetchProperties = useCallback(async (pageNum: number, append: boolean = false) => {
    if (!sellerId) return;

    try {
      const from = pageNum * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data: propertiesData, error: propertiesError, count } = await supabase
        .from('properties')
        .select(`
          id,
          address,
          city,
          state,
          zip_code,
          price,
          bedrooms,
          bathrooms,
          square_feet,
          image_url,
          created_at
        `, { count: 'exact' })
        .eq('seller_id', sellerId)
        .eq('status', statusFilter)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (propertiesError) {
        console.error("Error fetching properties:", propertiesError);
        setError("Failed to load properties");
        return;
      }

      const newProperties = propertiesData || [];
      const total = count || 0;

      if (append) {
        setProperties(prev => [...prev, ...newProperties]);
      } else {
        setProperties(newProperties);
      }

      setTotalCount(total);
      setHasMore((pageNum + 1) * PAGE_SIZE < total);
      setPage(pageNum);
    } catch (err) {
      console.error("Unexpected error fetching properties:", err);
      setError("An unexpected error occurred");
    }
  }, [sellerId, statusFilter]);

  useEffect(() => {
    if (isResolving) return;
    if (resolveError) {
      setIsLoading(false);
      setError(resolveError);
      return;
    }
    const fetchSellerAndListings = async () => {
      if (!sellerId) {
        setIsLoading(false);
        setError("No seller ID provided");
        return;
      }


      try {
        setIsLoading(true);
        setError(null);

        console.log("Fetching public seller data for ID:", sellerId);

        // Check authentication status for security filtering
        const { data: { user } } = await supabase.auth.getUser();
        const isAuthenticated = !!user;

        let sellerProfile: Seller;

        if (isAuthenticated) {
          // Authenticated users can get contact info through secure function
          const { data: publicData, error: publicError } = await (supabase as any)
            .rpc('get_public_seller_info', { seller_user_id: sellerId });
          
          if (publicError || !publicData || publicData.length === 0) {
            setError("Seller not found");
            return;
          }
          
          sellerProfile = {
            full_name: publicData[0].full_name,
            business_name: publicData[0].business_name,
            profile_image_url: publicData[0].profile_image_url,
            email: null,
            phone_number: null
          };
        } else {
          // Unauthenticated users get public info only
          const { data: publicData, error: publicError } = await (supabase as any)
            .rpc('get_public_seller_info', { seller_user_id: sellerId });
          
          if (publicError || !publicData || publicData.length === 0) {
            setError("Seller not found");
            return;
          }
          
          sellerProfile = {
            full_name: publicData[0].full_name,
            business_name: publicData[0].business_name,
            profile_image_url: publicData[0].profile_image_url,
            email: null,
            phone_number: null
          };
        }

        setSeller(sellerProfile);

        // Fetch first page of properties
        await fetchProperties(0, false);

        console.log("Properties loaded with pagination");
      } catch (err) {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSellerAndListings();
  }, [sellerId, isResolving, resolveError, fetchProperties]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    try {
      await fetchProperties(page + 1, true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, page, fetchProperties]);

  return { 
    properties, 
    seller, 
    isLoading, 
    error,
    totalCount,
    hasMore,
    loadMore,
    isLoadingMore
  };
};
